import { Navigate } from 'react-router-dom';
import { useAuth } from '@/shared/auth/AuthContext';
import useCapacites from '@/shared/auth/useCapacites';

/** Route d'accueil par défaut selon le rôle (là où on redirige après login
 *  ou quand un rôle tente d'accéder à une page qui ne lui est pas autorisée). */
export function defaultRouteForRole(role) {
  // Le poseur arrive sur le meme ecran que son installateur : c'est le parcours
  // de signature, et c'est son seul metier dans l'application.
  if (role === 'installateur' || role === 'poseur') return '/installateur';
  if (role === 'distributeur') return '/sav';
  // Le super admin n'a accès à AUCUNE page métier. Sans cette ligne il était
  // renvoyé vers /dashboard, qui le renvoyait vers sa page par défaut, donc
  // vers /dashboard : une boucle de redirection infinie dès la connexion.
  if (role === 'super_admin') return '/superadmin';
  return '/dashboard';
}

function Spinner() {
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

/**
 * Garde de route, par CAPACITE de preference et par role quand il le faut.
 * - non connecté                      -> /login
 * - capacité manquante                -> sa page d'accueil par défaut
 * - rôle non autorisé                 -> sa page d'accueil par défaut
 * - sinon                             -> rend la page.
 * `roles` et `capacite` omis = simple garde d'authentification.
 *
 * POURQUOI `capacite` EN PLUS DE `roles` (lot I). Une liste de rôles écrite ici est une
 * COPIE de la matrice du serveur, et elle dérive : `CRM = ['admin', 'commercial']`
 * laissait le gestionnaire ouvrir /facturation alors que le serveur allait lui refuser
 * les données. Le menu ne proposait plus l'entrée, mais l'adresse tapée à la main
 * menait toujours à un écran qui ne se remplissait pas. Une garde par capacité lit ce
 * que la route exige RÉELLEMENT et renvoie proprement à l'accueil.
 *
 * `roles` reste utile là où la règle n'est pas une capacité mais une appartenance : le
 * super_admin de la console de plateforme, le parcours terrain de l'installateur.
 *
 * ⚠️ Ni l'un ni l'autre n'est une protection. Le refus vit côté serveur ; ceci décide
 * seulement de ce qu'on ouvre plutôt que de laisser un écran vide.
 */
export default function RoleProtectedRoute({ roles, capacite, children }) {
  const { user, loading } = useAuth();
  const { possede, pret } = useCapacites();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={defaultRouteForRole(user.role)} replace />;
  }
  // Tant que les capacités ne sont pas connues, on ATTEND plutôt que de rediriger :
  // un renvoi à l'accueil au premier rendu ferait sortir de la page qu'on vient
  // d'ouvrir, et l'utilisateur ne saurait pas pourquoi.
  if (capacite && !pret) return <Spinner />;
  if (capacite && !possede(capacite)) {
    return <Navigate to={defaultRouteForRole(user.role)} replace />;
  }
  return children;
}
